import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="border border-black p-5" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</div>
      <div className="serif text-4xl mt-2 tabular-nums">{value}</div>
      {sub && <div className="mono text-[10px] uppercase tracking-[0.25em] text-amber-700 mt-2">{sub}</div>}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/analytics")
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return <p className="mono text-xs text-neutral-500">Loading analytics&hellip;</p>;
  if (!data) return <p className="mono text-xs text-neutral-500">No data.</p>;

  const totalVotes = (data.votes.poll || 0) + (data.votes.likert || 0);

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Editor&rsquo;s dashboard</div>
        <h2 className="serif text-3xl mt-1">Site analytics</h2>
        <p className="text-sm text-neutral-600 mt-2">
          Point-in-time counts drawn from the database. &ldquo;This week&rdquo; = last seven days.
        </p>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Registered users"
          value={data.users.total}
          sub={data.users.last_7d ? `+${data.users.last_7d} this week` : "0 this week"}
          testid="stat-users"
        />
        <StatCard
          label="Published pieces"
          value={data.posts.by_status.approved || 0}
          sub={data.posts.last_7d ? `${data.posts.last_7d} submitted this week` : "0 submitted this week"}
          testid="stat-posts"
        />
        <StatCard
          label="Approved replies"
          value={data.comments.approved || 0}
          sub={data.comments.last_7d ? `+${data.comments.last_7d} this week` : "0 this week"}
          testid="stat-comments"
        />
        <StatCard
          label="Votes cast"
          value={totalVotes}
          sub={data.votes.last_7d ? `+${data.votes.last_7d} this week` : "0 this week"}
          testid="stat-votes"
        />
      </div>

      {/* Content mix */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="border border-neutral-300 p-5">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Content mix</div>
          <ol className="space-y-2">
            {[
              ["essay", "Essays"],
              ["forum", "Forums"],
              ["poll", "Polls"],
              ["interview", "Interviews"],
            ].map(([key, label]) => {
              const n = data.posts.by_type[key] || 0;
              const denom = data.posts.total || 1;
              const pct = Math.round((n / denom) * 100);
              return (
                <li key={key} data-testid={`content-mix-${key}`}>
                  <div className="flex justify-between mono text-xs">
                    <span className="uppercase tracking-[0.2em] text-neutral-700">{label}</span>
                    <span className="tabular-nums text-neutral-500">{n} &middot; {pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-neutral-100 border border-neutral-200">
                    <div className="h-full" style={{ width: `${pct}%`, background: "#fc4707" }} />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="border border-neutral-300 p-5">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Moderation queue</div>
          <ul className="space-y-2 mono text-sm">
            <li className="flex justify-between">
              <span className="uppercase tracking-[0.2em] text-neutral-700">Posts pending</span>
              <span className="tabular-nums text-neutral-500">{data.posts.by_status.pending || 0}</span>
            </li>
            <li className="flex justify-between">
              <span className="uppercase tracking-[0.2em] text-neutral-700">Posts rejected</span>
              <span className="tabular-nums text-neutral-500">{data.posts.by_status.rejected || 0}</span>
            </li>
            <li className="flex justify-between">
              <span className="uppercase tracking-[0.2em] text-neutral-700">Replies pending</span>
              <span className="tabular-nums text-neutral-500">{data.comments.pending || 0}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Top polls */}
      <div className="border border-neutral-300 p-5">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Top polls by participation</div>
        {data.top_polls.length === 0 ? (
          <p className="mono text-xs text-neutral-500">No poll votes yet.</p>
        ) : (
          <ol className="space-y-3">
            {data.top_polls.map((p, i) => (
              <li key={p.id} className="flex items-baseline justify-between gap-4">
                <Link to={`/post/${p.id}`} className="serif text-lg hover:text-amber-800 transition-colors">
                  {i + 1}. {p.title}
                </Link>
                <span className="mono text-xs tabular-nums text-neutral-500 whitespace-nowrap">
                  {p.votes} vote{p.votes === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Top forums */}
      <div className="border border-neutral-300 p-5">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Most-discussed forums</div>
        {data.top_forums.length === 0 ? (
          <p className="mono text-xs text-neutral-500">No forum replies yet.</p>
        ) : (
          <ol className="space-y-3">
            {data.top_forums.map((f, i) => (
              <li key={f.id} className="flex items-baseline justify-between gap-4">
                <Link to={`/post/${f.id}`} className="serif text-lg hover:text-amber-800 transition-colors">
                  {i + 1}. {f.title} {f.closed && <span className="mono text-[10px] uppercase tracking-[0.25em] text-amber-700">(closed)</span>}
                </Link>
                <span className="mono text-xs tabular-nums text-neutral-500 whitespace-nowrap">
                  {f.replies} repl{f.replies === 1 ? "y" : "ies"}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
